import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, MessageCircle } from "lucide-react";
import confetti from "canvas-confetti";
import { Button } from "./ui/button";

const packages = [
  { name: "PAKET A", betina: "Rp 2.000.000", jantan: "Rp 2.500.000", sate: "200 Tusuk", gule: "40 Bungkus", berat: "± 14kg" },
  { name: "PAKET B", betina: "Rp 2.300.000", jantan: "Rp 2.800.000", sate: "250 Tusuk", gule: "50 Bungkus", berat: "± 18kg" },
  { name: "PAKET C", betina: "Rp 2.600.000", jantan: "Rp 3.100.000", sate: "300 Tusuk", gule: "60 Bungkus", berat: "± 22kg" },
  { name: "PAKET D", betina: "Rp 2.900.000", jantan: "Rp 3.400.000", sate: "350 Tusuk", gule: "70 Bungkus", berat: "± 26kg" },
  { name: "SUPER",   betina: "Rp 3.200.000", jantan: "Rp 3.700.000", sate: "400 Tusuk", gule: "80 Bungkus", berat: "± 28kg" },
  { name: "SUPER+",  betina: "Rp 3.500.000", jantan: "Rp 4.100.000", sate: "450 Tusuk", gule: "90 Bungkus", berat: "± 32kg" },
  { name: "ISTIMEWA", betina: "Rp 3.900.000", jantan: "Rp 4.400.000", sate: "500 Tusuk", gule: "100 Bungkus", berat: "± 36kg" },
  { name: "EKONOMIS", special: true, harga: "Rp 3.650.000", porsi: "80 Kotak Nasi Lengkap", berat: "±17 kg" },
];


const boxPackages = [
  { name: "A", hargaSatuan: 12500, desc: "Nasi box ekonomis dengan lauk sederhana", isi: ["Nasi", "Ayam Goreng", "Sambal", "Kerupuk", "Acar"] },
  { name: "B", hargaSatuan: 15500, desc: "Nasi box standar dengan lauk lengkap", isi: ["Nasi", "Ayam Bakar", "Sambal", "Kerupuk", "Acar", "Telur"] },
  { name: "C", hargaSatuan: 18500, desc: "Nasi box premium dengan lauk spesial", isi: ["Nasi", "Ayam Gulai", "Sambal", "Kerupuk", "Acar", "Telur", "Sayur Wortel Kubis"] },
  { name: "D", hargaSatuan: 20000, desc: "Nasi box deluxe dengan lauk premium", isi: ["Nasi", "Ayam Rica", "Sambal", "Kerupuk", "Acar", "Telur", "Sayur Wortel Kubis", "Lalapan"] },
  { name: "E", hargaSatuan: 23500, desc: "Nasi box VIP dengan lauk eksklusif", isi: ["Nasi", "Ayam Kremes", "Sambal", "Kerupuk", "Acar", "Telur", "Sayur Wortel Kubis", "Lalapan", "Buah"] },
];


const fasilitasDefault = [
  "Sertifikat + Bingkai",
  "Dokumentasi Foto",
  "Video Zoom Meeting Pemotongan",
  "Gratis ongkir <10km",
  "Subsidi ongkir >Rp 50.000,-",
];

const PricingPopup = ({ setHideWidgets }) => {
  const [selected, setSelected] = useState(null);
  const [menuChoice, setMenuChoice] = useState("");
  const [extraChoice, setExtraChoice] = useState("");
  const [kambingChoice, setKambingChoice] = useState("");
  const [formData, setFormData] = useState({ nama: "", alamat: "", tanggal: "" });
  const [showInvoice, setShowInvoice] = useState(false);
  const [selectedBox, setSelectedBox] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const popupRef = useRef(null);
  const invoiceRef = useRef(null);

  // ✅ Klik luar card untuk close + reset
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        resetSelections();
        setSelected(null);
      }
    };
    if (selected) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [selected]);

  // ✅ Klik luar invoice untuk close
  useEffect(() => {
    const handleClickOutsideInvoice = (e) => {
      if (invoiceRef.current && !invoiceRef.current.contains(e.target)) {
        setShowInvoice(false);
        setSelectedBox(null);
        setInvoiceData(null);
      }
    };
    if (showInvoice) {
      document.addEventListener("mousedown", handleClickOutsideInvoice);
      document.addEventListener("touchstart", handleClickOutsideInvoice);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideInvoice);
      document.removeEventListener("touchstart", handleClickOutsideInvoice);
    };
  }, [showInvoice]);

  // ✅ Hide widgets saat popup aktif
  useEffect(() => {
    if (setHideWidgets) {
      setHideWidgets(selected !== null || showInvoice);
    }
  }, [selected, showInvoice, setHideWidgets]);

  const resetSelections = () => {
    setMenuChoice("");
    setExtraChoice("");
    setKambingChoice("");
    setSelectedBox(null);
    setShowInvoice(false);
    setFormData({ nama: "", alamat: "", tanggal: "" });
  };

  // ✅ Toggle pilihan
  const handleMenuSelect = (menu) => {
    setMenuChoice(menuChoice === menu ? "" : menu);
  };
  const handleExtraSelect = (extra) => {
    setExtraChoice(extraChoice === extra ? "" : extra);
  };
  const handleKambingSelect = (type) => {
    setKambingChoice(kambingChoice === type ? "" : type);
  };

  const handleBoxSelect = (box) => {
    setSelectedBox(box);
  };

  const handleBoxWhatsApp = () => {
    if (!invoiceData || !selectedBox) return;
    const jumlahBungkus = parseInt(invoiceData.selected.gule.split(' ')[0]);
    const totalHarga = jumlahBungkus * selectedBox.hargaSatuan;
    const message = `Assalamualaikum, nama saya "${invoiceData.formData.nama}", alamat saya di "${invoiceData.formData.alamat}". Saya ingin memesan paket "${invoiceData.selected.name}" dengan opsi nasi box "${selectedBox.name}" (${jumlahBungkus} bungkus) total Rp ${totalHarga.toLocaleString()}, menu utama "${invoiceData.menuChoice}", extra menu "${invoiceData.extraChoice}" dan jenis kambing "${invoiceData.kambingChoice}". Pesanan untuk tanggal "${invoiceData.formData.tanggal}".`;

    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/6281295138575?text=${encoded}`, "_blank");
  };

  // ✅ Template pesan WA
  const handleWhatsAppClick = (pkg, boxData = null) => {
    // Only for regular packages (A–ISTIMEWA)
    const selectedPackageName = pkg.name;
    const selectedMainMenu = menuChoice;
    const selectedExtraMenu = extraChoice;
    const selectedGoatType = kambingChoice === "Betina" ? "Kambing Betina" : kambingChoice === "Jantan" ? "Kambing Jantan" : "";
    const selectedGoatPriceFormatted = kambingChoice === "Betina" ? pkg.betina : kambingChoice === "Jantan" ? pkg.jantan : "";
    const selectedGoatPriceNumber = kambingChoice === "Betina" ? parseInt(pkg.betina.replace(/[^\d]/g, '')) : kambingChoice === "Jantan" ? parseInt(pkg.jantan.replace(/[^\d]/g, '')) : 0;

    let isBoxOrder = false;
    let selectedBoxName = "";
    let selectedBoxPriceNumber = 0;
    let selectedBoxPriceFormatted = "";
    let totalPriceNumber = selectedGoatPriceNumber;
    let totalPriceFormatted = selectedGoatPriceFormatted;

    if (boxData && boxData.name && boxData.hargaSatuan) {
      isBoxOrder = true;
      selectedBoxName = `Paket ${boxData.name}`;
      // Calculate box price: jumlah bungkus x harga satuan
      const jumlahBungkus = pkg.gule ? parseInt(pkg.gule.split(' ')[0]) : 0;
      selectedBoxPriceNumber = jumlahBungkus * boxData.hargaSatuan;
      selectedBoxPriceFormatted = `Rp ${selectedBoxPriceNumber.toLocaleString()}`;
      totalPriceNumber = selectedGoatPriceNumber + selectedBoxPriceNumber;
      totalPriceFormatted = `Rp ${totalPriceNumber.toLocaleString()}`;
    } else {
      totalPriceNumber = selectedGoatPriceNumber;
      totalPriceFormatted = selectedGoatPriceFormatted;
    }

    let message =
      `Assalamualaikum, nama saya "${formData.nama}", alamat saya di "${formData.alamat}". ` +
      `Saya ingin memesan paket "${selectedPackageName}" dengan pilihan menu utama "${selectedMainMenu}", ` +
      `extra menu "${selectedExtraMenu}" dan jenis kambing "${selectedGoatType} - ${selectedGoatPriceFormatted}". `;

    if (isBoxOrder) {
      message += `Saya juga memilih Paket Nasi Box "${selectedBoxName}" dengan harga ${selectedBoxPriceFormatted}. `;
    }

    message += `Total harga: ${totalPriceFormatted}. `;
    message += `Pesanan untuk tanggal "${formData.tanggal}".`;

    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/6281295138575?text=${encoded}`, "_blank");
  };

  const handleKotakanClick = (pkg) => {
    confetti({
      particleCount: 80,
      spread: 50,
      origin: { y: 0.6 },
      colors: ["#58B09C", "#FF8A65", "#FFD8C8"],
    });
    setSelected(pkg);
  };

  return (
    <section
      id="pricing"
      className="relative py-14 px-4 bg-gradient-to-b from-[#FFFBF9] via-[#FFF7F4] to-[#FFFFFF]"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-5xl font-extrabold text-[#333] mb-3">
          Pilih <span className="text-[#FF8A65]">Paket Aqiqah</span>
        </h2>
        <p className="text-[#777] text-base md:text-lg max-w-2xl mx-auto">
          Klik salah satu paket untuk melihat detail harga dan fasilitasnya.
        </p>
      </div>

      {/* Tombol Paket */}
      <div className="flex flex-wrap justify-center gap-3">
        {packages.map((pkg, i) => (
          <motion.button
            key={i}
            onClick={() => (pkg.special ? handleKotakanClick(pkg) : setSelected(pkg))}
            whileTap={{ scale: 0.96 }}
            className={`px-4 py-2 text-xs sm:text-sm font-semibold rounded-xl shadow-md transition-all duration-200 ${
              pkg.special
                ? "bg-[#58B09C] text-white hover:bg-[#4AA18B]"
                : "bg-gradient-to-br from-[#FF8A65] to-[#FF6B4A] text-white hover:brightness-110"
            }`}
          >
            {pkg.special ? "EKONOMIS" : pkg.name}
          </motion.button>
        ))}
      </div>

      {/* Popup */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex justify-center items-center px-3 sm:px-4"
          >
            <motion.div
              ref={popupRef}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="relative bg-white rounded-3xl shadow-xl w-full max-w-md p-5 sm:p-6 overflow-y-auto max-h-[85svh] border border-[#FFD8C8]"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <button
                onClick={() => {
                  resetSelections();
                  setSelected(null);
                }}
                className="absolute top-2 right-2 text-[#FF8A65] hover:text-[#FF6B4A]"
              >
                <X className="w-5 h-5" />
              </button>

              {/* EKONOMIS */}
              {selected.special ? (
                <>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#58B09C] mb-4 text-center">PAKET EKONOMIS</h3>
                  <div className="text-[#777] mb-3 text-center text-sm leading-relaxed">Paket berisi: Nasi, Sate Non Tusuk, Gulai, Kerupuk, Acar dan Buah</div>
                  <div className="flex flex-col gap-3 mb-4">
                    {[
                      { name: "Paket 40 Box", price: "Rp 2.000.000" },
                      { name: "Paket 50 Box", price: "Rp 2.300.000" },
                      { name: "Paket 60 Box", price: "Rp 2.700.000" },
                      { name: "Paket 75 Box", price: "Rp 3.200.000" },
                      { name: "Paket 100 Box", price: "Rp 4.000.000" },
                    ].map((pkg, idx) => (
                      <button
                        key={pkg.name}
                        type="button"
                        onClick={() => setSelectedBox(pkg)}
                        className={`w-full rounded-2xl border p-4 text-left shadow-sm transition-all font-semibold ${selectedBox?.name === pkg.name ? "bg-[#58B09C] text-white border-[#58B09C]" : "bg-[#EAF7F4] text-[#333] border-[#D4F0E8]"}`}
                      >
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-base font-bold">{pkg.name}</span>
                          <span className="text-lg">{pkg.price}</span>
                        </div>
                        {/* Description removed as requested */}
                      </button>
                    ))}
                  </div>
                  {/* Description moved above, removed here */}
                  <ul className="text-sm text-[#555] space-y-1 mb-4">
                    {fasilitasDefault.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#25D366]" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-[#777] mb-2 text-center italic">Harap isi detail di bawah untuk memesan:</p>
                  <div className="space-y-2 mb-4">
                    <input
                      type="text"
                      placeholder="Nama Anda"
                      value={formData.nama}
                      onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                      className="w-full border border-[#ccc] rounded-lg p-2 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Alamat Lengkap"
                      value={formData.alamat}
                      onChange={(e) => setFormData({ ...formData, alamat: e.target.value })}
                      className="w-full border border-[#ccc] rounded-lg p-2 text-sm"
                    />
                    <p className="text-xs text-[#777] mb-1">Tanggal Acara Aqiqah</p>
                    <input
                      type="date"
                      value={formData.tanggal}
                      onChange={(e) => setFormData({ ...formData, tanggal: e.target.value })}
                      className="w-full border border-[#ccc] rounded-lg p-2 text-sm"
                    />
                  </div>
                  <Button
                    disabled={!selectedBox || !formData.nama || !formData.alamat || !formData.tanggal}
                    onClick={() => {
                      const message = `Assalamualaikum, nama saya "${formData.nama}", alamat saya di "${formData.alamat}". Saya ingin memesan paket EKONOMIS (${selectedBox?.name} - ${selectedBox?.price}), pesanan untuk tanggal "${formData.tanggal}".`;
                      const encoded = encodeURIComponent(message);
                      window.open(`https://wa.me/6281295138575?text=${encoded}`, "_blank");
                    }}
                    className={`w-full bg-[#58B09C] hover:bg-[#4AA18B] text-white py-3 rounded-xl font-semibold text-base flex items-center justify-center gap-2 shadow-md ${selectedBox && formData.nama && formData.alamat && formData.tanggal ? "" : "opacity-60 cursor-not-allowed"}`}
                  >
                    <MessageCircle className="w-5 h-5" /> Pesan via WhatsApp
                  </Button>
                </>
              ) : (
                <>
                  {/* Paket Biasa */}
                  <h3 className="text-xl font-bold text-[#333] mb-2 text-center">
                    {selected.name}
                  </h3>
                  <p className="text-[#777] text-sm text-center mb-3 leading-relaxed">
                    Pilih menu utama & extra menu di bawah ini:
                  </p>

                  {/* Main Menu */}
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-[#333] mb-2 text-center">Menu Utama</p>
                    <div className="flex justify-center gap-3">
                      {["Sate", "Semur"].map((m) => (
                        <button
                          key={m}
                          onClick={() => handleMenuSelect(m)}
                          className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${
                            menuChoice === m
                              ? "bg-[#FF8A65] text-white"
                              : "bg-[#FFEDE8] text-[#FF8A65] hover:bg-[#FFD8C8]"
                          }`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Extra Menu */}
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-[#333] mb-2 text-center">Extra Menu</p>
                    <div className="flex justify-center gap-3">
                      {["Sop", "Gule"].map((e) => (
                        <button
                          key={e}
                          onClick={() => handleExtraSelect(e)}
                          className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${
                            extraChoice === e
                              ? "bg-[#58B09C] text-white"
                              : "bg-[#EAF7F4] text-[#58B09C] hover:bg-[#D4F0E8]"
                          }`}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Kambing */}
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-[#333] mb-2 text-center">Pilihan Kambing</p>
                    <div className="flex flex-wrap justify-center gap-3 text-sm">
                    {[
                      { label: "Kambing Betina", value: "Betina", harga: selected.betina },
                      { label: "Kambing Jantan", value: "Jantan", harga: selected.jantan },
                    ].map((k) => (
                      <button
                        key={k.value}
                        onClick={() => handleKambingSelect(k.value)}
                        className={`p-2 rounded-xl border text-center transition-all ${
                          kambingChoice === k.value
                            ? "bg-[#FF8A65] text-white border-[#FF8A65] font-bold"
                            : "bg-[#FFF5F2] border-[#FFD8C8] text-[#333] font-semibold"
                        }`}
                      >
                        <p className={`text-xs ${kambingChoice === k.value ? 'text-white' : 'text-[#FF8A65]'}`}>{k.label}</p>
                        <p className={`text-sm ${kambingChoice === k.value ? 'text-white' : 'text-[#FF8A65]'}`}>{k.harga}</p>
                      </button>
                    ))}
                  </div>
                  </div>

                  {/* Info Paket dan Fasilitas */}
                  <ul className="text-[10px] text-[#555] space-y-1 mb-4">
                    <li className="flex items-start gap-2">
                      <Check className="w-2 h-2 text-[#25D366]" />
                      Berat: {selected.berat}
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-2 h-2 text-[#25D366]" />
                      Sate: {selected.sate}
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-2 h-2 text-[#25D366]" />
                      Gule/Semur/Sop: {selected.gule}
                    </li>
                    {fasilitasDefault.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-2 h-2 text-[#25D366]" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <p className="text-[11px] text-[#777] mb-2 text-center italic">
                    Harap isi detail di bawah untuk memesan:
                  </p>

                  {/* Form */}
                  <div className="space-y-2 mb-4">
                    <input
                      type="text"
                      placeholder="Nama Anda"
                      value={formData.nama}
                      onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                      className="w-full border border-[#ccc] rounded-lg p-2 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Alamat Lengkap"
                      value={formData.alamat}
                      onChange={(e) => setFormData({ ...formData, alamat: e.target.value })}
                      className="w-full border border-[#ccc] rounded-lg p-2 text-sm"
                    />
                    <p className="text-xs text-[#777] mb-1">Tanggal Acara Aqiqah</p>
                    <input
                      type="date"
                      value={formData.tanggal}
                      onChange={(e) => setFormData({ ...formData, tanggal: e.target.value })}
                      className="w-full border border-[#ccc] rounded-lg p-2 text-sm"
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        setInvoiceData({
                          selected,
                          menuChoice,
                          extraChoice,
                          kambingChoice,
                          formData
                        });
                        setShowInvoice(true);
                        setSelected(null);
                      }}
                      className="flex-1 py-3 rounded-xl font-semibold text-base flex items-center justify-center gap-2 shadow-md bg-[#FF8A65] hover:bg-[#E67A5A] text-white"
                    >
                      Jadikan box?
                    </Button>
                    <Button
                      disabled={!menuChoice || !extraChoice || !kambingChoice}
                      onClick={() => handleWhatsAppClick(selected)}
                      className={`flex-1 py-3 rounded-xl font-semibold text-base flex items-center justify-center gap-2 shadow-md ${
                        menuChoice && extraChoice && kambingChoice
                          ? "bg-[#25D366] hover:bg-[#128C7E] text-white"
                          : "bg-gray-300 text-gray-600 cursor-not-allowed"
                      }`}
                    >
                      <MessageCircle className="w-5 h-5" /> Pesan Sekarang
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invoice Popup */}
      <AnimatePresence>
        {showInvoice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex justify-center items-center px-3 sm:px-4"
          >
            <motion.div
              ref={invoiceRef}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="relative bg-white rounded-3xl shadow-xl w-full max-w-lg p-4 sm:p-6 overflow-y-auto max-h-[75svh] md:max-h-[90svh] border border-[#FFD8C8]"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <button
                onClick={() => {
                  setShowInvoice(false);
                  setSelectedBox(null);
                  setInvoiceData(null);
                }}
                className="absolute top-2 right-2 text-[#FF8A65] hover:text-[#FF6B4A]"
              >
                <X className="w-5 h-5" />
              </button>

              <h3 className="text-xl font-bold text-[#333] mb-4 text-center">
                Invoice Pesanan Nasi Box
              </h3>

              {/* Detail Pesanan */}
              <div className="space-y-3 mb-4">
                <div className="flex justify-between" style={{ borderBottom: '1px dotted #ccc', paddingBottom: '4px' }}>
                  <span className="text-[#555]">Paket:</span>
                  <span className="font-semibold">{invoiceData?.selected?.name}</span>
                </div>
                <div className="flex justify-between" style={{ borderBottom: '1px dotted #ccc', paddingBottom: '4px' }}>
                  <span className="text-[#555]">Menu Utama:</span>
                  <span className="font-semibold">{invoiceData?.menuChoice}</span>
                </div>
                <div className="flex justify-between" style={{ borderBottom: '1px dotted #ccc', paddingBottom: '4px' }}>
                  <span className="text-[#555]">Extra Menu:</span>
                  <span className="font-semibold">{invoiceData?.extraChoice}</span>
                </div>
                <div className="flex justify-between" style={{ borderBottom: '1px dotted #ccc', paddingBottom: '4px' }}>
                  <span className="text-[#555]">Jenis Kambing:</span>
                  <span className="font-semibold">{invoiceData?.kambingChoice}</span>
                </div>
                <div className="flex justify-between" style={{ borderBottom: '1px dotted #ccc', paddingBottom: '4px' }}>
                  <span className="text-[#555]">Jumlah Bungkus:</span>
                  <span className="font-semibold">{invoiceData?.selected ? parseInt(invoiceData.selected.gule.split(' ')[0]) : 0}</span>
                </div>
              </div>

              {/* Pilih Paket Box */}
              <div className="mb-4">
                <p className="text-sm font-semibold text-[#333] mb-2">Pilih Paket Nasi Box:</p>
                <div className="grid grid-cols-1 gap-2">
                  {boxPackages.map((box, i) => {
                    const jumlahBungkus = invoiceData?.selected ? parseInt(invoiceData.selected.gule.split(' ')[0]) : 0;
                    const totalHarga = jumlahBungkus * box.hargaSatuan;
                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedBox(box)}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          selectedBox?.name === box.name
                            ? "bg-[#FF8A65] text-white border-[#FF8A65] font-bold"
                            : "bg-[#FFF5F2] border-[#FFD8C8] text-[#333] font-semibold"
                        }`}
                      >
                        <div className="flex justify-between items-center mb-1">
                          <span>Paket {box.name}</span>
                          <span>Rp {totalHarga.toLocaleString()}</span>
                        </div>
                        <p className={`text-xs ${selectedBox?.name === box.name ? 'text-white' : 'text-[#333]'}`}>Isi: {box.isi.join(", ")}</p>
                        <p className={`text-xs mt-1 ${selectedBox?.name === box.name ? 'text-white' : 'text-[#333]'}`}> ({jumlahBungkus} x Rp {box.hargaSatuan.toLocaleString()})</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Total */}
              <div className="border-t pt-4 mb-4 space-y-2">
                <div className="flex justify-between" style={{ borderBottom: '1px dotted #ccc', paddingBottom: '4px' }}>
                  <span>Harga Kambing:</span>
                  <span>Rp {invoiceData?.selected ? parseInt((invoiceData.kambingChoice === "Betina" ? invoiceData.selected.betina : invoiceData.selected.jantan).replace(/[^\d]/g, '')) : 0}</span>
                </div>
                <div className="flex justify-between" style={{ borderBottom: '1px dotted #ccc', paddingBottom: '4px' }}>
                  <span>Harga Nasi Box:</span>
                  <span>Rp {selectedBox && invoiceData?.selected ? (parseInt(invoiceData.selected.gule.split(' ')[0]) * selectedBox.hargaSatuan).toLocaleString() : 0}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Total Harga:</span>
                  <span>Rp {(() => {
                    if (!invoiceData?.selected || !selectedBox) return 0;
                    const hargaKambing = parseInt((invoiceData.kambingChoice === "Betina" ? invoiceData.selected.betina : invoiceData.selected.jantan).replace(/[^\d]/g, ''));
                    const hargaBox = parseInt(invoiceData.selected.gule.split(' ')[0]) * selectedBox.hargaSatuan;
                    return (hargaKambing + hargaBox).toLocaleString();
                  })()}</span>
                </div>
              </div>

              {/* Form */}
              <div className="space-y-2 mb-4">
                <input
                  type="text"
                  placeholder="Nama Anda"
                  value={invoiceData?.formData?.nama || ""}
                  onChange={(e) => setInvoiceData({ ...invoiceData, formData: { ...invoiceData.formData, nama: e.target.value } })}
                  className="w-full border border-[#ccc] rounded-lg p-2 text-sm"
                />
                <input
                  type="text"
                  placeholder="Alamat Lengkap"
                  value={invoiceData?.formData?.alamat || ""}
                  onChange={(e) => setInvoiceData({ ...invoiceData, formData: { ...invoiceData.formData, alamat: e.target.value } })}
                  className="w-full border border-[#ccc] rounded-lg p-2 text-sm"
                />
                <p className="text-xs text-[#777] mb-1">Tanggal Acara Aqiqah</p>
                <input
                  type="date"
                  value={invoiceData?.formData?.tanggal || ""}
                  onChange={(e) => setInvoiceData({ ...invoiceData, formData: { ...invoiceData.formData, tanggal: e.target.value } })}
                  className="w-full border border-[#ccc] rounded-lg p-2 text-sm"
                />
              </div>

              <Button
                disabled={!selectedBox || !invoiceData?.formData?.nama || !invoiceData?.formData?.alamat || !invoiceData?.formData?.tanggal}
                onClick={() => {
                  // WhatsApp message for box mode (A–ISTIMEWA)
                  if (!selectedBox || !invoiceData) return;
                  const pkg = invoiceData.selected;
                  const selectedPackageName = pkg.name;
                  const selectedMainMenu = invoiceData.menuChoice;
                  const selectedExtraMenu = invoiceData.extraChoice;
                  const selectedGoatType = invoiceData.kambingChoice === "Betina" ? "Kambing Betina" : invoiceData.kambingChoice === "Jantan" ? "Kambing Jantan" : "";
                  const selectedGoatPriceFormatted = invoiceData.kambingChoice === "Betina" ? pkg.betina : invoiceData.kambingChoice === "Jantan" ? pkg.jantan : "";
                  const selectedGoatPriceNumber = invoiceData.kambingChoice === "Betina" ? parseInt(pkg.betina.replace(/[^\d]/g, '')) : invoiceData.kambingChoice === "Jantan" ? parseInt(pkg.jantan.replace(/[^\d]/g, '')) : 0;
                  const jumlahBungkus = pkg.gule ? parseInt(pkg.gule.split(' ')[0]) : 0;
                  const selectedBoxName = `Paket ${selectedBox.name}`;
                  const selectedBoxPriceNumber = jumlahBungkus * selectedBox.hargaSatuan;
                  const selectedBoxPriceFormatted = `Rp ${selectedBoxPriceNumber.toLocaleString()}`;
                  const totalPriceNumber = selectedGoatPriceNumber + selectedBoxPriceNumber;
                  const totalPriceFormatted = `Rp ${totalPriceNumber.toLocaleString()}`;
                  let message =
                    `Assalamualaikum, nama saya "${invoiceData.formData.nama}", alamat saya di "${invoiceData.formData.alamat}". ` +
                    `Saya ingin memesan paket "${selectedPackageName}" dengan pilihan menu utama "${selectedMainMenu}", ` +
                    `extra menu "${selectedExtraMenu}" dan jenis kambing "${selectedGoatType} - ${selectedGoatPriceFormatted}". `;
                  message += `Saya juga memilih Paket Nasi Box "${selectedBoxName}" dengan harga ${selectedBoxPriceFormatted}. `;
                  message += `Total harga: ${totalPriceFormatted}. `;
                  message += `Pesanan untuk tanggal "${invoiceData.formData.tanggal}".`;
                  const encoded = encodeURIComponent(message);
                  window.open(`https://wa.me/6281295138575?text=${encoded}`, "_blank");
                }}
                className={`w-full py-3 rounded-xl font-semibold text-base flex items-center justify-center gap-2 shadow-md ${
                  selectedBox && invoiceData?.formData?.nama && invoiceData?.formData?.alamat && invoiceData?.formData?.tanggal
                    ? "bg-[#25D366] hover:bg-[#128C7E] text-white"
                    : "bg-gray-300 text-gray-600 cursor-not-allowed"
                }`}
              >
                <MessageCircle className="w-5 h-5" /> Pesan Nasi Box
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PricingPopup;